<template>
  <div class="quality-text">
    <Input
      v-if="poll.mask === textMask"
      class="quality-text__input"
      @input="handleText"
      :placeholder="computedPlaceholder"
      :value="results[questionnaireUid].answers[poll.uid].answer[0]"
    />
    <EmailInput
      v-else-if="poll.mask === emailMask"
      class="quality-text__input"
      @getValue="handleEmail"
      :email-value="computedEmailAnswer"
    />
    <PhoneInput
      v-else-if="poll.mask === phoneMask"
      class="quality-text__input"
      @input="handlePhone"
      :value="results[questionnaireUid].answers[poll.uid].answer[0]"
    />
    <SelectInput
      v-else-if="poll.mask === cityMask"
      class="quality-text__input"
      @input="handleSelectInput"
      @search="handleSelectSearch"
      @focus="handleSelectFocus"
      :select-data="citiesList"
      label="name"
      :placeholder="$t('start_tap_to_search')"
      :value="currentCity"
      :empty-error="emptyError"
      :calculate-dropdown-position="simpleQuestion"
    />
    <template v-else>
      <label for="quality-text" class="quality-text__label visually-hidden">{{ poll.title }}</label>
      <textarea
        name="quality-text"
        id="quality-text"
        cols="30"
        rows="5"
        class="quality-text__textarea control"
        :placeholder="computedPlaceholder"
        :maxlength="maxCommentLength"
        :value="results[questionnaireUid].answers[poll.uid].answer[0]"
        @focus="$emit('focus')"
        @blur="$emit('blur')"
        @input="handleInput"></textarea>
      <span class="quality-text__allowed-length">{{`Допустима кількість символів: ${currentCommentLength}/${maxCommentLength}`}}</span>
    </template>
  </div>
</template>

<script>
import { debounce } from '../../mixins'
import { mapState, mapGetters, mapActions } from 'vuex'
import EmailInput from '../inputs/EmailInput'
import PhoneInput from '../inputs/PhoneInput'
import SelectInput from '../inputs/SelectInput'
import Input from '../inputs/Input'

export default {
  name: 'Textarea',
  components: { Input, SelectInput, PhoneInput, EmailInput },
  mixins: [debounce],
  props: {
    poll: {
      type: Object,
      required: true
    },
    results: Object,
    questionnaireUid: String,
    emailMask: String,
    phoneMask: String,
    textMask: String,
    cityMask: String,
    simpleQuestion: Boolean
  },
  data () {
    return {
      maxCommentLength: 1000,
      currentCommentLength: 0,
      cities: {},
      currentCity: null,
      emptyError: ''
    }
  },
  computed: {
    ...mapGetters(['urlLanguagePrefix', 'isUkLanguage']),
    ...mapState({
      userData: state => state.user.user_data,
      selectedUserLocation: state => state.geoLocation.selectedUserLocation
    }),
    citiesList () {
      if (this.cities?.cities?.length) return this.cities.cities
      return []
    },
    computedEmailAnswer () {
      if (this.poll.mask === this.emailMask) {
        return this.results[this.questionnaireUid].answers[this.poll.uid].answer[0] ? this.results[this.questionnaireUid].answers[this.poll.uid].answer[0] : this.userData?.email ? this.userData.email : ''
      }

      return ''
    },
    computedCityAnswer () {
      if (this.poll.mask === this.cityMask) {
        return this.results[this.questionnaireUid].answers[this.poll.uid].answer[0] ? this.results[this.questionnaireUid].answers[this.poll.uid].answer[0] : this.userData?.email ? this.userData.email : ''
      }

      return ''
    },
    computedPlaceholder () {
      if (this.poll.hint) return this.poll.hint

      return 'Ваша відповідь'
    }
  },
  methods: {
    ...mapActions('questionnaire', ['SET_ANSWER_ACTION', 'GET_CITIES']),
    handleInput ({ target }) {
      const lineBreaks = (target.value.match(/\n/g) || []).length

      this.currentCommentLength = target.value.length + lineBreaks

      if (this.currentCommentLength >= this.maxCommentLength) {
        this.currentCommentLength = this.maxCommentLength
      }

      this.SET_ANSWER_ACTION({ uid: this.questionnaireUid, answerUid: this.poll.uid, answer: [target.value], type: this.poll.type })
    },
    handleEmail (data) {
      const { value } = data

      this.SET_ANSWER_ACTION({ uid: this.questionnaireUid, answerUid: this.poll.uid, answer: [value], type: this.poll.type })
    },
    handlePhone (phone) {
      this.SET_ANSWER_ACTION({ uid: this.questionnaireUid, answerUid: this.poll.uid, answer: ['0' + phone], type: this.poll.type })
    },
    handleText (text) {
      this.SET_ANSWER_ACTION({ uid: this.questionnaireUid, answerUid: this.poll.uid, answer: [text], type: this.poll.type })
    },
    handleSelectFocus () {
      this.emptyError = this.$t('enter_3_characters_geo')
    },
    handleSelectSearch (query) {
      if (query.length < 2) {
        this.cities = []
        this.emptyError = this.$t('enter_3_characters_geo')
      } else {
        this.debounce(() => {
          this.GET_CITIES({ city: query }).then(res => {
            if (!res.length) this.emptyError = this.$t('not_found_geo')

            this.cities = res
          })
        }, 100)
      }
    },
    handleSelectInput (value) {
      this.currentCity = value

      this.SET_ANSWER_ACTION({ uid: this.questionnaireUid, answerUid: this.poll.uid, answer: [value.uid], type: this.poll.type })
    }
  },
  mounted () {
    if (this.poll.mask === this.cityMask) {
      const cityUid = this.results[this.questionnaireUid].answers[this.poll.uid].answer[0]

      if (cityUid) {
        this.GET_CITIES({ city_uid: cityUid }).then(res => {
          this.currentCity = res
          this.currentCity.name = this.isUkLanguage ? this.currentCity.name_uk : this.currentCity.name_ru
        })
      }
    }
  }
}
</script>

<style scoped lang="sass">

.quality-text
  display: flex
  flex-direction: column

.quality-text__label
  font-style: normal
  font-weight: normal
  font-size: 16px
  line-height: 150%
  padding-bottom: 20px

.quality-text__textarea
  resize: none
  padding: 10px
  font-size: 14px

.quality-text__allowed-length
  margin-top: 10px
  font-size: 12px
  font-weight: 400
  color: $placeholder-color

.quality-text__input
  max-width: 300px
</style>
